import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName, SubscriptionType } from "schemas/dashboard";
import { routes } from "schemas/routes";
import { LIGHT_GREY } from "styles/oneOffColor";

import SeeExample from "components/StripeCheckout/components/SeeExample";
import SubscriptionStatusText from "features/Home/UpgradeDialog/Plan/SubscriptionStatusText";
import { PlanAction } from "features/Home/UpgradeDialog/Plan/helpers";
import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CardWrapper from "../components/CardWrapper";

const AddOnPartnershipData = () => {
  const source = getSource();
  const navigate = useNavigate();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    subscription,
    plans,
    addOns,
    setOpenCheckoutDialog,
    upgradeDialogSource,
  } = useContext(SubscriptionContext);
  const { checkoutLoading, onPlanButtonClick } = useSubscription();

  const getButtonName = (): {
    title: string;
    variant: "text" | "outlined" | "contained";
    action: () => void;
  } => {
    if (subscription && subscription?.partnershipData) {
      if (
        subscription?.partnershipData?.cancelAt &&
        subscription?.partnershipData?.status === SubscriptionType.ACTIVE
      ) {
        return {
          title: "Renew",
          variant: "contained",
          action: () =>
            onPlanButtonClick(AddOnItemName.partnership_data, PlanAction.renew),
        };
      } else if (
        subscription?.partnershipData?.status === SubscriptionType.CANCELED
      ) {
        return {
          title: "Resubscribe",
          variant: "contained",
          action: handleCheckout,
        };
      } else if (
        subscription?.partnershipData?.status === SubscriptionType.INCOMPLETE
      ) {
        return {
          title: "Retry",
          variant: "contained",
          action: handleCheckout,
        };
      } else {
        return {
          title: "Manage Add-on",
          variant: "outlined",
          action: () =>
            onPlanButtonClick(
              AddOnItemName.partnership_data,
              PlanAction.manage,
            ),
        };
      }
    } else {
      return {
        title: "Purchase",
        variant: "contained",
        action: handleCheckout,
      };
    }
  };

  const handleCheckout = async () => {
    if (!plans || !addOns) return;
    const state: CheckoutState = {
      plan: AddOnItemName.partnership_data,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  return (
    <CardWrapper>
      <Grid item>
        <Typography variant="h5" component="div" gutterBottom>
          Partnership Data{" "}
        </Typography>

        <Typography variant="h6" sx={{ color: "green.main" }} gutterBottom>
          ${addOns?.[AddOnItemName.partnership_data]?.price.toFixed(2)}
          /mo
          <SeeExample />
        </Typography>

        <Box sx={{ backgroundColor: LIGHT_GREY, p: 2, borderRadius: 3, my: 2 }}>
          <Typography>
            Users can purchase on Free plan or Pro plan. <br />
            <strong>
              Partnership data is included in the{" "}
              {currentOrg?.showAutopilotPlan
                ? "Autopilot"
                : "Virtual Assistant"}{" "}
              plan.
            </strong>
          </Typography>
        </Box>

        <Typography variant="h6" sx={{ my: 2 }}>
          🔥 Access insider data on brand partnerships
        </Typography>

        <Typography gutterBottom>
          See if they pay or just offer gifted products, when they last
          collaborated, where they operate, and if their contacts are verified
          or responsive.
        </Typography>
      </Grid>

      <Grid item>
        {currentOrg?.isVaUser || currentOrg?.isAutopilotUser ? (
          <Typography sx={{ color: "green.main", my: 2 }}>
            <strong>
              Already Included{" "}
              <Box component="i" className="fa-solid fa-circle-check" />
            </strong>
          </Typography>
        ) : (
          <>
            <LoadingButton
              onClick={getButtonName()?.action}
              disableElevation
              variant={
                getButtonName()?.title === "Purchase" ? "contained" : "text"
              }
              loading={checkoutLoading === AddOnItemName.partnership_data}
              sx={{
                px: 2,
                color:
                  getButtonName()?.title === "Purchase"
                    ? "white"
                    : "primary.main",
                backgroundColor:
                  getButtonName()?.title === "Purchase"
                    ? "primary.main"
                    : "#f4f8f6",
              }}
            >
              {getButtonName()?.title}
            </LoadingButton>
          </>
        )}
      </Grid>
      {!currentOrg?.isVaUser && !currentOrg?.isAutopilotUser && (
        <Typography>
          <SubscriptionStatusText
            status={
              subscription?.partnershipData?.status || SubscriptionType.TRIALING
            }
            cancelAt={subscription?.partnershipData?.cancelAt}
          />
        </Typography>
      )}
    </CardWrapper>
  );
};

export default AddOnPartnershipData;
